import * as React from 'react'

import {
  Avatar,
  BlankSlate,
  Box,
  Circle,
  Divider,
  Grid,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  PerformancePastReviewsSidepanelDocument,
  ReviewAnswerEvaluationFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { BoxedCallout } from 'modules/performance_process/components/common/BoxedCallout'
import { colorPicker } from 'modules/performance_process/components/common/filters/colorPicker'
import { reviewTypePicker } from 'modules/performance_process/components/common/filters/reviewTypePicker'
import { mapQuestionnaireResponse } from 'modules/performance_process/lib/questionnaireForStrategies'
import { QuestionnaireResponse } from 'modules/questionnaires/components/QuestionnaireResponse'

const EmptyState = () => (
  <BlankSlate
    title={i18n.t(
      'performance_process.reviews.answer_evaluation.past_reviews_section.empty_state.title'
    )}
    description={i18n.t(
      'performance_process.reviews.answer_evaluation.past_reviews_section.empty_state.description'
    )}
    illustration={'document-edit'}
    size={'small'}
  />
)

export const PastReviewsSidepanel: React.FC<{
  open: boolean
  targetAccessId: number
  reviewProcessStartedAt: string
  onClose: () => void
}> = ({ targetAccessId, reviewProcessStartedAt, open, onClose }) => {
  const [selectedEvaluation, setSelectedEvaluation] = React.useState<
    ReviewAnswerEvaluationFragment | undefined
  >(undefined)
  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(undefined)

  const { data } = useQuery(PerformancePastReviewsSidepanelDocument, {
    variables: {
      targetAccessId,
      reviewProcessStartedAt,
    },
    onCompleted: ({ performance }) => {
      const firstEvaluation = performance.reviewEvaluationsConnection.nodes?.[0]
      if (!firstEvaluation) return

      setSelectedEvaluation(firstEvaluation)
      setSelectedIndex(0)
    },
  })

  const selectEvaluation = (evaluation: ReviewAnswerEvaluationFragment, index: number) => {
    setSelectedEvaluation(evaluation)
    setSelectedIndex(index)
  }

  const publishedEvaluations = data?.performance.reviewEvaluationsConnection.nodes ?? []
  const selectedEvaluationAnswers = selectedEvaluation?.answeredQuestionnaires
  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      width="wideXL"
      title={i18n.t('performance_process.reviews.answer_evaluation.past_reviews')}
    >
      <Box gap="s32">
        {!publishedEvaluations.length ? (
          <EmptyState />
        ) : (
          <>
            <Grid variant="fixed">
              {publishedEvaluations.map((evaluation, i) => (
                <Box
                  gridColSpan={{ xs: 6 }}
                  key={i}
                  borderRadius={{ all: 'abs016' }}
                  border={{
                    all: {
                      color: selectedIndex == i ? 'viridian1000' : 'grey400',
                      width: 's1',
                      style: 'solid',
                    },
                  }}
                  onClick={() => selectEvaluation(evaluation, i)}
                  padding={'s16'}
                  gap="s16"
                >
                  <Text size="400" color="textPrimary" weight="semibold">
                    {evaluation.performanceReviewProcess?.name || ''}
                  </Text>

                  <Box flexDirection="row" gap="s12" alignItems="center">
                    <Avatar
                      size="s32"
                      url={buildUrl(evaluation.reviewerAccess?.avatar)}
                      label={evaluation.reviewerAccess?.fullName || ''}
                    />
                    <Box>
                      <Box gap="s8">
                        <Text.Normal weight="medium">
                          {evaluation.reviewerAccess?.fullName}
                        </Text.Normal>
                        <Box flexDirection="row" gap="s8">
                          <Box alignSelf="center">
                            <Circle
                              color={colorPicker(evaluation.evaluationType || '')}
                              size={16}
                            />
                          </Box>
                          <Text.Normal>
                            {reviewTypePicker(evaluation.evaluationType || '')}
                          </Text.Normal>
                          <Text.Normal weight="semibold">
                            ({moment(evaluation.publishedAt).format('D MMM YYYY')})
                          </Text.Normal>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Divider />
            {selectedEvaluation && (
              <>
                <QuestionnaireResponse
                  key={`${selectedEvaluation.id}-answered-questionnaire`}
                  readonly
                  content={mapQuestionnaireResponse(
                    selectedEvaluationAnswers?.answeredQuestionnaireWithSections
                  )}
                  onAnswer={() => null}
                  emptyState={<></>}
                />
                <QuestionnaireResponse
                  key={`${selectedEvaluation.id}-answered-competencies-questionnaire`}
                  readonly
                  content={mapQuestionnaireResponse(
                    selectedEvaluationAnswers?.answeredCompetenciesQuestionnaire
                  )}
                  onAnswer={() => null}
                  emptyState={<></>}
                />
                <QuestionnaireResponse
                  key={`${selectedEvaluation.id}-answered-employee-score-questionnaire`}
                  readonly
                  content={mapQuestionnaireResponse(
                    selectedEvaluationAnswers?.answeredEmployeeScoreQuestionnaire
                  )}
                  onAnswer={() => null}
                  emptyState={<></>}
                />
                <QuestionnaireResponse
                  key={`${selectedEvaluation.id}-answered-employee-potential-score-questionnaire`}
                  readonly
                  content={mapQuestionnaireResponse(
                    selectedEvaluationAnswers?.answeredEmployeePotentialScoreQuestionnaire
                  )}
                  onAnswer={() => null}
                  emptyState={<></>}
                  renderSectionFooter={() => (
                    <BoxedCallout
                      description={i18n.t(
                        'performance_process.employee_potential_score.questionnaire.callout'
                      )}
                    />
                  )}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Sidepanel>
  )
}
